import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plane, Upload, ArrowLeft, FileText, Receipt, Plus, Trash2, DollarSign, Eye, Download, X, Ticket, Hotel, CreditCard, FileCheck, Image } from 'lucide-react';
import { travelApi, analyticsApi, tokenApi } from '@/lib/api';
import { toast } from 'sonner';
import { formatDate, formatCurrency, fileToBase64, isAuthenticated } from '@/lib/utils';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import SponsorSection from '@/components/SponsorSection';
import { ModuleChat } from '@/components/ModuleChat';

const CATEGORY_COLORS = {
  airfare: '#2563EB',
  accommodation: '#7C3AED',
  restaurants: '#EC4899',
  transport: '#F59E0B',
  attractions: '#10B981'
};

const CATEGORY_LABELS = {
  airfare: 'Airfare',
  accommodation: 'Accommodation',
  restaurants: 'Restaurants',
  transport: 'Transport',
  attractions: 'Attractions'
};

const DOC_TYPE_CONFIG = {
  ticket: { label: 'Airline Ticket', icon: Ticket, color: 'text-blue-600 bg-blue-50' },
  hotel: { label: 'Hotel Booking', icon: Hotel, color: 'text-purple-600 bg-purple-50' },
  passport: { label: 'Passport Copy', icon: FileCheck, color: 'text-red-600 bg-red-50' },
  receipt: { label: 'Receipt', icon: Receipt, color: 'text-amber-600 bg-amber-50' },
  visa: { label: 'Visa', icon: FileCheck, color: 'text-emerald-600 bg-emerald-50' },
  insurance: { label: 'Travel Insurance', icon: CreditCard, color: 'text-teal-600 bg-teal-50' },
  itinerary: { label: 'Itinerary', icon: FileText, color: 'text-indigo-600 bg-indigo-50' },
  other: { label: 'Other Document', icon: FileText, color: 'text-slate-600 bg-slate-50' },
};

export default function TravelModule() {
  const { tokenId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [activeTrip, setActiveTrip] = useState('Tokyo Business Trip');
  const [documents, setDocuments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({ by_category: {}, grand_total: 0 });
  const [sponsorInfo, setSponsorInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Dialogs
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [documentType, setDocumentType] = useState('ticket');
  
  // Expense form
  const [expenseMerchant, setExpenseMerchant] = useState('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('restaurants');
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate(`/t/${tokenId}`);
      return;
    }
    loadData();
    loadSponsorInfo();
  }, [tokenId, activeTrip]);

  const loadData = async () => {
    try {
      const [docsRes, expensesRes, summaryRes] = await Promise.all([
        travelApi.getDocuments(tokenId, activeTrip),
        travelApi.getExpenses(tokenId, activeTrip),
        travelApi.getExpenseSummary(tokenId, activeTrip)
      ]);
      setDocuments(docsRes.data);
      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load travel data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSponsorInfo = async () => {
    try {
      const response = await tokenApi.getSponsorInfo(tokenId, 'travel');
      setSponsorInfo(response.data);
    } catch (error) {
      console.log('No sponsor info available');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const base64 = await fileToBase64(file);
      setDocumentPreview({ file, base64 });
      setShowAddDocument(true);
    } catch (error) {
      toast.error('Failed to process file');
    }
  };

  const handleUploadDocument = async () => {
    if (!documentPreview) return;

    setIsSubmitting(true);
    try {
      await travelApi.createDocument(tokenId, documentPreview.base64, documentType, activeTrip);
      toast.success('Document uploaded!');
      setShowAddDocument(false);
      setDocumentPreview(null);
      loadData();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddExpense = async () => {
    if (!expenseMerchant || !expenseAmount) return;

    setIsSubmitting(true);
    try {
      await travelApi.createExpense(
        tokenId, 
        expenseMerchant, 
        parseFloat(expenseAmount), 
        expenseCategory, 
        activeTrip, 
        null
      );
      toast.success('Expense added!');
      setShowAddExpense(false);
      setExpenseMerchant('');
      setExpenseAmount('');
      loadData();
    } catch (error) {
      toast.error('Failed to add expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEndTrip = async () => {
    if (!confirm('End this trip? Receipt images will be deleted but summary will be kept.')) return;
    
    try {
      await travelApi.endTrip(tokenId, activeTrip);
      toast.success('Trip ended! Summary saved.');
      loadData();
    } catch (error) {
      toast.error('Failed to end trip');
    }
  };

  const handleOpenDocument = async (doc) => {
    setIsLoadingDocument(true);
    setShowDocumentViewer(true);
    try {
      const response = await travelApi.getDocumentFile(doc.id);
      setViewingDocument(response.data);
    } catch (error) {
      toast.error('Failed to load document');
      setShowDocumentViewer(false);
    } finally {
      setIsLoadingDocument(false);
    }
  };

  const handleDownloadDocument = () => {
    if (!viewingDocument?.file_base64) return;
    const isImage = !viewingDocument.file_base64.startsWith('JVB'); // PDF starts with %PDF -> JVBERi in base64
    const mimeType = isImage ? 'image/png' : 'application/pdf';
    const ext = isImage ? 'png' : 'pdf';
    const link = document.createElement('a');
    link.href = `data:${mimeType};base64,${viewingDocument.file_base64}`;
    link.download = `${viewingDocument.document_type || 'document'}-${viewingDocument.id?.slice(0, 8)}.${ext}`;
    link.click();
  };

  const chartData = Object.entries(summary.by_category).map(([category, data]) => ({
    name: CATEGORY_LABELS[category] || category,
    value: data.total,
    color: CATEGORY_COLORS[category] || '#94A3B8'
  }));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="travel-module">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="back-to-dashboard">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                <Plane className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h1 className="font-bold text-lg text-slate-900">Memora Travel Tag</h1>
                <p className="text-xs text-slate-500 font-mono">{tokenId}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={handleEndTrip}
              className="rounded-full"
              data-testid="end-trip-btn"
            >
              End Trip
            </Button>
            <Button 
              onClick={() => fileInputRef.current?.click()}
              className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6"
              data-testid="upload-document-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Add Document
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      </header>

      {/* Trip Selector */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500">Current Trip:</span>
            <span className="font-semibold text-slate-900">{activeTrip}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Expenses Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 sticky top-32">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Trip Expenses</h2>
              
              <div className="text-center mb-6">
                <p className="text-4xl font-bold text-slate-900">
                  {formatCurrency(summary.grand_total)}
                </p>
                <p className="text-sm text-slate-500">Total Spend</p>
              </div>

              {chartData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-40 flex items-center justify-center text-slate-400">
                  No expenses yet
                </div>
              )}

              <Button
                onClick={() => setShowAddExpense(true)}
                className="w-full mt-4 bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                data-testid="add-expense-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Expense
              </Button>
            </div>
          </div>

          {/* Right Column - Documents & Expenses List */}
          <div className="lg:col-span-2 space-y-8">
            {/* Documents */}
            <section>
              <h2 className="text-lg font-bold text-slate-900 mb-4">Documents</h2>
              
              {documents.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No documents uploaded yet</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {documents.map((doc, index) => {
                    const typeConfig = DOC_TYPE_CONFIG[doc.document_type] || DOC_TYPE_CONFIG.other;
                    const TypeIcon = typeConfig.icon;
                    return (
                      <motion.div
                        key={doc.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer group"
                        onClick={() => handleOpenDocument(doc)}
                        data-testid={`document-${doc.id}`}
                      >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${typeConfig.color}`}>
                          <TypeIcon className="w-6 h-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900">{typeConfig.label}</p>
                          <p className="text-sm text-slate-500 truncate">{formatDate(doc.uploaded_at)}</p>
                          {doc.trip_name && <p className="text-xs text-slate-400">{doc.trip_name}</p>}
                        </div>
                        <Eye className="w-5 h-5 text-slate-300 group-hover:text-teal-500 transition-colors flex-shrink-0" />
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Expenses List */}
            <section>
              <h2 className="text-lg font-bold text-slate-900 mb-4">Expenses</h2>
              
              {expenses.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
                  <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No expenses recorded yet</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
                  {expenses.map((expense, index) => (
                    <motion.div
                      key={expense.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      className="p-4 flex items-center gap-4"
                      data-testid={`expense-${expense.id}`}
                    >
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${CATEGORY_COLORS[expense.category]}15` }}
                      >
                        <DollarSign 
                          className="w-5 h-5" 
                          style={{ color: CATEGORY_COLORS[expense.category] }} 
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900">{expense.merchant_name}</p>
                        <p className="text-sm text-slate-500 capitalize">{CATEGORY_LABELS[expense.category]}</p>
                      </div>
                      <p className="font-semibold text-slate-900">
                        {formatCurrency(expense.amount)}
                      </p>
                    </motion.div>
                  ))}
                </div>
              )}
            </section>

            {/* Sponsor Section */}
            <SponsorSection 
              sponsorInfo={sponsorInfo} 
              tokenId={tokenId} 
              moduleType="travel" 
              className="mt-8"
            />
          </div>
        </div>
      </main>

      <ModuleChat sponsorInfo={sponsorInfo} />

      {/* Add Document Dialog */}
      <Dialog open={showAddDocument} onOpenChange={setShowAddDocument}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Document</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {documentPreview && (
              <div className="aspect-video rounded-xl overflow-hidden bg-slate-100 flex items-center justify-center">
                {documentPreview.file.type.startsWith('image/') ? (
                  <img 
                    src={`data:${documentPreview.file.type};base64,${documentPreview.base64}`}
                    alt="Preview"
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <FileText className="w-16 h-16 text-slate-400" />
                )}
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Document Type
              </label>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger className="rounded-xl" data-testid="document-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ticket">Airline Ticket</SelectItem>
                  <SelectItem value="hotel">Hotel Booking</SelectItem>
                  <SelectItem value="passport">Passport Copy</SelectItem>
                  <SelectItem value="receipt">Receipt</SelectItem>
                  <SelectItem value="visa">Visa</SelectItem>
                  <SelectItem value="insurance">Travel Insurance</SelectItem>
                  <SelectItem value="itinerary">Itinerary</SelectItem>
                  <SelectItem value="other">Other Document</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button
              onClick={handleUploadDocument}
              disabled={isSubmitting || !documentPreview}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full"
              data-testid="confirm-document-btn"
            >
              {isSubmitting ? 'Uploading...' : 'Upload Document'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Expense Dialog */}
      <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Expense</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Merchant Name
              </label>
              <Input
                placeholder="Restaurant, Hotel, etc."
                value={expenseMerchant}
                onChange={(e) => setExpenseMerchant(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="merchant-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Amount (USD)
              </label>
              <Input
                type="number"
                placeholder="0.00"
                value={expenseAmount}
                onChange={(e) => setExpenseAmount(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="amount-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Category
              </label>
              <Select value={expenseCategory} onValueChange={setExpenseCategory}>
                <SelectTrigger className="rounded-xl" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="airfare">Airfare</SelectItem>
                  <SelectItem value="accommodation">Accommodation</SelectItem>
                  <SelectItem value="restaurants">Restaurants</SelectItem>
                  <SelectItem value="transport">Transport</SelectItem>
                  <SelectItem value="attractions">Attractions</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button
              onClick={handleAddExpense}
              disabled={isSubmitting || !expenseMerchant || !expenseAmount}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full"
              data-testid="confirm-expense-btn"
            >
              {isSubmitting ? 'Adding...' : 'Add Expense'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog open={showDocumentViewer} onOpenChange={(open) => { setShowDocumentViewer(open); if (!open) setViewingDocument(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewingDocument && (() => {
                const typeConfig = DOC_TYPE_CONFIG[viewingDocument.document_type] || DOC_TYPE_CONFIG.other;
                const TypeIcon = typeConfig.icon;
                return (
                  <>
                    <TypeIcon className="w-5 h-5" />
                    {typeConfig.label}
                  </>
                );
              })()}
            </DialogTitle>
            <DialogDescription>View your uploaded travel document</DialogDescription>
          </DialogHeader>

          {isLoadingDocument ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : viewingDocument?.file_base64 ? (
            <div className="space-y-4">
              <div className="rounded-xl overflow-hidden bg-slate-100 max-h-[60vh] overflow-auto">
                {(() => {
                  const b64 = viewingDocument.file_base64;
                  const isPdf = b64.startsWith('JVB');
                  if (isPdf) {
                    return (
                      <iframe
                        src={`data:application/pdf;base64,${b64}`}
                        className="w-full h-[58vh]"
                        title="Document viewer"
                        data-testid="document-viewer-pdf"
                      />
                    );
                  }
                  return (
                    <img
                      src={`data:image/png;base64,${b64}`}
                      alt={viewingDocument.document_type}
                      className="w-full h-auto object-contain"
                      data-testid="document-viewer-image"
                    />
                  );
                })()}
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-500">
                  {viewingDocument.trip_name && <span>{viewingDocument.trip_name}</span>}
                  {viewingDocument.uploaded_at && <span className="ml-2">{formatDate(viewingDocument.uploaded_at)}</span>}
                </div>
                <Button
                  variant="outline"
                  onClick={handleDownloadDocument}
                  className="rounded-full"
                  data-testid="download-document-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          ) : viewingDocument?.file_url ? (
            <div className="space-y-4">
              <div className="rounded-xl overflow-hidden bg-slate-100 max-h-[60vh] overflow-auto flex items-center justify-center">
                <img
                  src={viewingDocument.file_url}
                  alt={viewingDocument.document_type}
                  className="w-full h-auto object-contain"
                  data-testid="document-viewer-url"
                />
              </div>
              <div className="text-sm text-slate-500">
                {viewingDocument.trip_name && <span>{viewingDocument.trip_name}</span>}
                {viewingDocument.uploaded_at && <span className="ml-2">{formatDate(viewingDocument.uploaded_at)}</span>}
              </div>
            </div>
          ) : (
            <div className="py-12 text-center text-slate-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Document file is no longer available</p>
              <p className="text-xs mt-1">Files are removed when a trip ends</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
